import React, { useEffect, useMemo, useState } from "react";
import style from "./index.module.scss";
import {
    Button,
    Col,
    Input,
    InputNumber,
    message,
    Row,
    Select,
    Spin
} from "antd";
import FieldWithError from "components/FieldWithError";
import Form, { useForm, useWatch } from "rc-field-form";
import className from "classnames";
import FileList from "../../../../../FileList";
import { errorMessage } from "service/service";
import { ResultMessage } from "components/ResultMessage";
import Footer from "../../../Footer";
import useRequestFn from "utils/useRequestFn";
import {
    ActivityTaskDetail,
    deleteTaskFile,
    getNotTaskActivityGroup,
    saveGroupActivityTaskInfo,
    TaskFileEntityListItem
} from "service/group";
import Confirm from "../../../Confirm";
import {
    ActivityIdAtom,
    emptyFormAtom,
    ShowConfirmAtom,
    StepStateAtom,
    TouchedAtom
} from "../../../../../../store";
import { useAtom } from "jotai";
import { useAtomValue } from "jotai";
import ChainUpload from "components/ChainUpload";
import { ChainUploadFileProps } from "components/ChainUpload/servers";
import Config from "config";
import { PublishStatus } from "service/group";
import { humanFileSize } from "service/service";
import SubmitError from "components/SubmitError";
import useRequest from "utils/useRequest";

interface formDataProps {
    taskName: string; //活动名称
    taskQuestion: string; //截止时间
    groupIds: number[]; //参与小组
    score: number;
}

interface TaskFormProps {
    status?: PublishStatus;
    taskId: number; //任务id
    onChangeTaskId: (val: number) => void; //改变当前任务id
    taskDetail: ActivityTaskDetail | null;
    onChangeStep: (val: number) => void;
    onCancel: () => void;
    loading: boolean;
    onRefresh: () => void; //更新列表
}

export default function TaskForm({
    status,
    taskId,
    onChangeTaskId,
    taskDetail,
    onCancel,
    onChangeStep,
    loading,
    onRefresh
}: TaskFormProps) {
    const { TextArea } = Input;
    const { Option } = Select;
    const [form] = useForm<formDataProps>();
    const [touched, setTouched] = useAtom(TouchedAtom);
    const activityId = useAtomValue(ActivityIdAtom); //活动id
    const [stepState, setStepState] = useAtom(StepStateAtom);
    const [showConfirm, setShowConfirm] = useAtom(ShowConfirmAtom);
    const [emptyForm, setEmptyForm] = useAtom(emptyFormAtom);
    const [msg, setMsg] = useState(false);

    const [saveGroupInfo, { loading: saveLoading }] = useRequestFn(
        saveGroupActivityTaskInfo
    );

    //上传的附件列表
    const [file, setFile] = useState<TaskFileEntityListItem[]>([]);

    useEffect(() => {
        if (!emptyForm) {
            form.setFieldsValue({
                taskName: taskDetail?.taskName,
                taskQuestion: taskDetail?.taskQuestion,
                groupIds: taskDetail?.groupIds?.split(","),
                score: taskDetail?.score || 100
            });

            setFile(taskDetail?.taskFileEntityList || []);
        }
    }, [taskDetail]);

    // 表单变更时修改touched
    function handleValuesChange() {
        setTouched(form.isFieldsTouched());
    }

    const [groupList] = useRequest(
        async () => {
            if (activityId !== -1) {
                return getNotTaskActivityGroup(
                    activityId,
                    taskId === -1 ? undefined : taskId
                );
            } else {
                return [];
            }
        },
        {},
        [activityId, taskId]
    );

    //获取无任务的剩余小组
    const groupIds = useWatch("groupIds", form);

    const canNextSave = useMemo(() => {
        if (!groupList) {
            return true;
        }
        return groupList?.length - groupIds?.length === 0;
    }, [groupIds, groupIds]);

    //编辑或保存
    async function onFinish(data: formDataProps) {
        const params = {
            ...data,
            id: taskId !== -1 ? taskId : undefined,
            activityId,
            groupIds: data.groupIds?.join(","),
            taskFileEntityList: file.map(item => ({
                id: item.id,
                fileName: item.fileName,
                fileUrl: item.fileUrl,
                fileSizeNum: item.fileSizeNum
            }))
        };
        try {
            await saveGroupInfo(params);
            await onRefresh();
            await onReset();
            emptyForm && resetForm();
            msg && message.success("任务保存成功");
        } catch (e) {
            ResultMessage.error(
                <SubmitError title="任务保存失败" content={errorMessage(e)} />,
                { width: 300 }
            );
        }
    }

    //验证失败,表单有必填项未输入时关闭遮罩
    function onFinishFailed(data: any) {
        if (data.errorFields.length !== 0) {
            setShowConfirm(false);
        }
    }

    //上传附件
    function onUploadSuccess(Files: ChainUploadFileProps[]) {
        const files = Files.filter(item => item.status === "done");

        const newFiles = files.map(item => ({
            key: item.response?.key,
            fileName: item.file.name,
            fileUrl: `${Config.QiNiuDownloadUrl}/${item.response?.key}`,
            fileSizeNum: item.file.size,
            fileSize: humanFileSize(item.file.size)
        }));
        setTouched(true);
        setFile([...file, ...newFiles]);
    }

    const onDeleteFile = async (params: TaskFileEntityListItem) => {
        //未保存前前端删
        if (params.key) {
            const files = file.filter(item => item.key !== params.key);
            setFile(files);
            return;
        }
        //保存后调用接口删
        try {
            await deleteTaskFile(params?.id);
            const files = file.filter(item => item.id !== params.id);
            setFile(files);
        } catch (e) {
            ResultMessage.error(
                <SubmitError title="删除失败" content={errorMessage(e)} />,
                { width: 300 }
            );
        }
    };

    //保存
    const onSave = () => {
        form.submit();
        setMsg(true);
        setEmptyForm(false);
    };

    //保存并继续添加任务
    const onAddNewForm = () => {
        form.submit();
        setEmptyForm(true);
    };

    //切换上一步
    const onChangeStepPrev = () => {
        //有未提交则等待提交后跳转
        if (touched) {
            setStepState("waitPrev");
            setShowConfirm(true);
        } else {
            onReset();
            setEmptyForm(false);
            onChangeStep(0);
        }
    };

    //切换下一步
    const onChangeStepNext = () => {
        form.submit();
        setStepState("next");
    };

    //二次弹窗，确认保存
    const onOkByModal = () => {
        form.submit();
        setEmptyForm(false);

        if (stepState !== "close") {
            switch (stepState) {
                case "waitPrev":
                    setStepState("prev");
                    break;
                case "waitNext":
                    setStepState("next");
                    break;
                default:
                    setStepState("current");
            }
            return;
        }
        setStepState("close");
    };

    //二次弹窗，不保存
    const onClose = () => {
        onReset();
        setEmptyForm(false);

        if (stepState === "current") {
            return;
        }

        if (["waitPrev", "prev"].includes(stepState)) {
            onChangeStep(0);
            return;
        }

        if (["waitNext", "next"].includes(stepState)) {
            onChangeStep(2);
            return;
        }
        onCancel();
    };

    //重置，清空表单内容
    const resetForm = () => {
        onChangeTaskId(-1);

        form.setFieldsValue({
            taskName: "",
            taskQuestion: "",
            groupIds: [],
            score: 100
        });

        setFile([]);
    };

    const handleClose = () => {
        if (touched) {
            setStepState("close");
            setShowConfirm(true);
            return;
        }
        onCancel();
    };

    //重置所有状态
    const onReset = () => {
        setShowConfirm(false);
        setTouched(false);
        setMsg(false);
        setStepState("current");
        stepState === "close" && onCancel();
        ["next", "prev"].includes(stepState) &&
            onChangeStep(stepState === "next" ? 2 : 0);
    };

    return (
        <div className={style.root}>
            <Spin spinning={loading}>
                <Form
                    form={form}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    onValuesChange={handleValuesChange}>
                    <Row>
                        <div className={style.label}>&nbsp;&nbsp; 任务名称</div>
                        <Form.Field name="taskName" initialValue="">
                            <Input
                                maxLength={200}
                                className={style.taskName}
                                style={{ width: "385px" }}
                                placeholder="请输入任务名称"
                            />
                        </Form.Field>
                    </Row>
                    <div>
                        <div
                            className={style.label}
                            style={{ display: "block" }}>
                            <i className={style.required}>*</i>任务描述
                        </div>
                        <FieldWithError
                            name="taskQuestion"
                            initialValue=""
                            rules={[
                                {
                                    required: true,
                                    message: "请输入任务描述"
                                }
                            ]}>
                            <TextArea
                                placeholder="请输入任务描述"
                                rows={8}
                                maxLength={1000}
                                className={style.description}
                            />
                        </FieldWithError>
                    </div>
                    <Row>
                        <div className={style.label}>
                            <i className={style.required}>*</i>参与小组
                        </div>
                        <Col span={12}>
                            <FieldWithError
                                name="groupIds"
                                initialValue={[]}
                                rules={[
                                    {
                                        required: true,
                                        message: "请选择参与小组"
                                    }
                                ]}>
                                <Select
                                    disabled={status === PublishStatus.Waiting}
                                    mode="multiple"
                                    allowClear
                                    className={style.select}
                                    placeholder="请选择参与小组"
                                    showArrow>
                                    {groupList?.map(item => (
                                        <Option
                                            value={item?.id.toString()}
                                            key={item?.id}>
                                            {item.groupName}
                                        </Option>
                                    ))}
                                </Select>
                            </FieldWithError>
                        </Col>
                        <div className={className(style.label, style.score)}>
                            <i className={style.required}>*</i>分值
                        </div>
                        <Col span={4}>
                            <FieldWithError
                                name="score"
                                initialValue={100}
                                rules={[
                                    {
                                        required: true,
                                        message: "请输入分值"
                                    }
                                ]}>
                                <InputNumber
                                    min={1}
                                    max={100}
                                    defaultValue={100}
                                    placeholder="请输入"
                                />
                            </FieldWithError>
                        </Col>
                    </Row>
                    <Row>
                        <div className={style.label}>
                            &nbsp;&nbsp; 附件数（{file.length}）
                        </div>
                        <Col span={3}>
                            <ChainUpload
                                onCancel={() => {}}
                                onUploadSuccess={onUploadSuccess}
                                tokenUrl="/teacher/getUpToken"
                                uploadUrl="https://up-z1.qiniup.com">
                                <div className={style.uploadBtn}>添加附件</div>
                            </ChainUpload>
                        </Col>
                    </Row>
                    <div className={style.fileList}>
                        {file.length !== 0 &&
                            file.map(item => (
                                <FileList
                                    key={item.key || item.id}
                                    fileName={item.fileName}
                                    fileSize={item?.fileSize}
                                    isPreview={false}
                                    onDelete={() => onDeleteFile(item)}
                                />
                            ))}
                    </div>
                </Form>
            </Spin>

            {showConfirm && <Confirm onCancel={onClose} onOk={onOkByModal} />}

            <Footer>
                <Button onClick={handleClose}>取消</Button>
                <Button type="primary" ghost onClick={onChangeStepPrev}>
                    上一步
                </Button>

                <Button
                    type="primary"
                    ghost
                    onClick={onSave}
                    loading={saveLoading}>
                    保存
                </Button>

                <Button
                    disabled={canNextSave}
                    type="primary"
                    ghost
                    onClick={onAddNewForm}
                    loading={saveLoading}>
                    继续添加任务
                </Button>

                <Button type="primary" onClick={onChangeStepNext}>
                    下一步
                </Button>
            </Footer>
        </div>
    );
}
